import {
    ActionMenuBadge,
    AuthContext,
    ClaimCloseoutType,
    ClaimPayerSummary,
    ClaimResponsibilityType,
    ClaimType,
    CollectionPaymentSourceType,
    Emoji,
    FacilityBadge,
    formatNumber,
    formatDate,
    getResponsibilityDescription,
    IActionMenuBadgeItem,
    IAuthContextProps,
    InsuranceOverview,
    Invoice,
    InvoiceType,
    LoadingMeter,
    MissingIdBadge,
    parseAmount,
    PersonalOverview,
    ProviderBadge,
    ProviderChargemasterItem,
    ProviderClaim,
    ProviderClaimEligibility,
    ProviderClaimPayment,
    ProviderCoding,
    ProviderCollectionPayment,
    ProviderComment,
    ProviderDocument,
    ProviderFacility,
    ProviderGuarantor,
    ProviderInstitutionalClaim,
    ProviderInstitutionalClaimTemplate,
    ProviderLetter,
    ProviderMail,
    ProviderMessage,
    ProviderNynf3Claim,
    ProviderNynf3ClaimTemplate,
    ProviderPayer,
    ProviderPayerSubscriber,
    ProviderPaymentPlan,
    ProviderPhone,
    ProviderProfessionalClaim,
    ProviderProfessionalClaimTemplate,
    ProviderProvider,
    ProviderReceiveFax,
    ProviderSeller,
    ProviderSendEmail,
    ProviderSendFax,
    ProviderStatus,
    ProviderSubscriber,
    ProviderSubscriberPatient,
    RecordOfServices,
    SellerBadge,
    SupportCheckoutCharge,
    ProviderCollectionStatusType,
    SupportCreditCard,
    SupportHL7Message,
    ToastContext,
    Tooltip,
    Transaction,
    truncateUuid,
    HorizontalStackedBarChart,
    ClaimChartData,
    renderString,
    calculateChartDataFromProfessionalServices,
    calculateChartDataFromInstitutionalServices,
    getEmptyChartData,
} from '@enter/core';
import { mdiPlus, mdiAlertCircle, mdiPause, mdiCheck, mdiAccountOutline, mdiPencilOutline } from '@mdi/js';
import { AiAssistant } from '@enter/core/src/ui/ai-assistant';
import { Fade } from '@mui/material';
import {
    IProviderMessagingContextProps,
    PageLayout,
    PageLayoutBreadcrumb,
    PageLayoutBreadcrumbLink,
    ProviderMessagingContext,
} from 'components';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { DialogContext, ErrorDialog, IDialogContextProps } from 'services';
import { withRouter, RouterProps } from 'router/with-router';
import { parseError } from 'services/dialog/util';
import {
    ClaimDetailsKey,
    ClaimStore,
    IClaimDetail,
    IInjectedRootStore,
    IObservedRootStore,
    MessageStore,
    RootStore,
} from 'stores';
import { ClaimService } from 'stores/claim.service';
import * as S from './claim-detail.styles';
import { BillingTimelineTable, ClaimTimelineTable, GuarantorHeader, InvoiceItemDialog } from './components';
import { CollectionPaymentDialog } from './components/collection-payment-dialog';
import { CollectionRefundDialog } from './components/collection-refund-dialog';
import { MaxProviderPRDialog } from './components/maxpr-provider-dialog';
import { ProviderCloseoutDialog } from './closeout-dialog/closeout-dialog';
import { ClaimBundle } from 'components/badges/claim-bundle';
import qs from "qs";
import Icon from '@mdi/react';
import enterLogo from '@enter/core/src/ui/ai-assistant/enter-services-icon.svg';
import { useAiExtensionConnectionStatus } from '@enter/core';

const formatAmount = formatNumber('Currency');

interface IMatchParams {
    claimId: string;
    accountId?: string;
}

interface IClaimDetailProps extends RouterProps<IMatchParams>, IInjectedRootStore {
    authContext: IAuthContextProps;
    messagingContext: IProviderMessagingContextProps;
    dialogContext: IDialogContextProps;
}

interface IClaimDetailState {
    claim: ProviderClaim | undefined;
    claimDetail: IClaimDetail | undefined;
    claimBundle: ProviderClaim[];
    loading: boolean;
    loadingBundle: boolean;
    showAlert: boolean;
    legacy?: string;
}

@observer
class ClaimDetailPage extends React.Component<IClaimDetailProps, IClaimDetailState> {
    private claimId: string;
    constructor(props: IClaimDetailProps) {
        super(props);
        this.claimId = this.props.match.params.claimId;
        if (this.claimId === undefined) {
            throw Error('No such claim');
        }

        const parseQueryParams = <T extends {}>(location: Location, initial: T): T => {
            const params = location.search.substring(1);
            const parsed = qs.parse(params);
            const picked = _.pick(parsed, _.keys(initial));
            return _.defaults(picked, initial);
        }

        const claimDetailsParams = parseQueryParams(location, {
            legacy: '',
        });

        this.state = {
            claim: undefined,
            claimDetail: undefined,
            claimBundle: [],
            loading: false,
            loadingBundle: false,
            showAlert: false,
            legacy: claimDetailsParams.legacy
        };
    }
    public componentDidMount(): void {
        window.scrollTo(0, 0);
        this.authContext.setGroup('claim-detail');
        this.fetchData();
        this.messagingContext.closePanel?.();
    }
    public componentDidUpdate(): void {
        const claimId = this.props.match.params.claimId;
        if (claimId !== this.claimId) {
            this.claimId = claimId;
            this.fetchData();
        }
    }
    public componentWillUnmount(): void {
        this.authContext.cancelGroup('claim-detail');
    }
    public render(): React.ReactElement {
        return this.state.claim ? this.renderPage() : this.renderLoader();
    }

    private get authContext(): IAuthContextProps {
        return this.props.authContext;
    }
    private get dialogContext(): IDialogContextProps {
        return this.props.dialogContext;
    }
    private get messagingContext(): IProviderMessagingContextProps {
        return this.props.messagingContext;
    }
    private get claimStore(): ClaimStore {
        return this.props.rootStore.claimStore;
    }
    private get messageStore(): MessageStore {
        return this.props.rootStore.messageStore;
    }

    private get templateLoaded(): boolean {
        return (
            this.isLoaded(['professionalTemplate']) ||
            this.isLoaded(['institutionalTemplate']) ||
            this.isLoaded(['nynf3Template'])
        );
    }
    // get additional claim details for Professional/Institutional/Nynf3/Dental types
    private get claimPIND(): ProviderProfessionalClaim | ProviderInstitutionalClaim | ProviderNynf3Claim | undefined {
        if (this.state.claim) {
            switch (this.state.claim.type) {
                case ClaimType.PROFESSIONAL:
                    return this.claimStore.findProfessionalClaim(this.claimId);
                case ClaimType.INSTITUTIONAL:
                    return this.claimStore.findInstitutionalClaim(this.claimId);
                case ClaimType.NYNF3:
                    return this.claimStore.findNynf3Claim(this.claimId);
                default:
                    throw new Error(`Unsupported Claim type: ${this.state.claim.type}`);
            }
        }
        return undefined;
    }
    private get collections(): ProviderCollectionPayment[] | undefined {
        return this.claimStore.findCollections(this.state.claim);
    }
    private get payments(): ProviderClaimPayment[] | undefined {
        return this.claimStore.findPayments(this.state.claim);
    }
    private get payers(): ProviderPayer[] | undefined {
        return _.compact(_.map(this.payerSubscribers, 'payer'));
    }
    private get providers(): ProviderProvider[] | undefined {
        return this.claimStore.findProviders(this.claimId);
    }
    private get subscribers(): ProviderSubscriber[] | undefined {
        return _.compact(_.map(this.payerSubscribers, 'subscriber'));
    }

    private get payerSubscribers(): ProviderPayerSubscriber[] | undefined {
        return this.claimStore.findPayerSubscribersByClaim(this.claimId);
    }
    private get subscriberPatients(): ProviderSubscriberPatient[] | undefined {
        return this.claimStore.findSubscriberPatientsByClaim(this.claimId);
    }
    private get statuses(): ProviderStatus[] | undefined {
        return this.claimStore.findStatuses(this.claimId);
    }
    private get transmissions(): ProviderStatus[] | undefined {
        return this.claimStore.findTransmissions(this.claimId);
    }
    private get documents(): ProviderDocument[] | undefined {
        return this.claimStore.findDocuments(this.claimId);
    }
    private get eligibilities(): ProviderClaimEligibility[] | undefined {
        return this.claimStore.findEligibilities(this.claimId);
    }
    private get facility(): ProviderFacility | undefined {
        return _.first(this.claimStore.findFacilities(this.claimId));
    }
    private get guarantor(): ProviderGuarantor | undefined {
        return _.first(this.claimStore.findGuarantors(this.claimId));
    }
    private get hl7Messages(): SupportHL7Message[] | undefined {
        return this.claimStore.findHL7Messages(this.claimId);
    }
    private get checkoutCharges(): SupportCheckoutCharge[] | undefined {
        return this.claimStore.findCheckoutCharges(this.guarantor);
    }
    private get creditCards(): SupportCreditCard[] | undefined {
        return this.claimStore.findCreditCards(this.guarantor);
    }
    private get paymentPlans(): ProviderPaymentPlan[] | undefined {
        return this.claimStore.findPaymentPlansByGuarantorId(_.get(this.guarantor, 'id', ''));
    }
    private get codings(): ProviderCoding[] | undefined {
        return this.claimStore.findCodings(this.claimId);
    }
    private get comments(): ProviderComment[] | undefined {
        return this.claimStore.findComments(this.claimId);
    }
    private get invoices(): Invoice[] | undefined {
        return this.claimStore.findInvoices(this.claimId);
    }
    private get institutionalTemplate(): ProviderInstitutionalClaimTemplate | undefined {
        return this.claimStore.findInstitutionalTemplate(this.claimId);
    }
    private get nynf3Template(): ProviderNynf3ClaimTemplate | undefined {
        return this.claimStore.findNynf3Template(this.claimId);
    }
    private get professionalTemplate(): ProviderProfessionalClaimTemplate | undefined {
        return this.claimStore.findProfessionalTemplate(this.claimId);
    }
    private get template():
        | ProviderProfessionalClaimTemplate
        | ProviderInstitutionalClaimTemplate
        | ProviderNynf3ClaimTemplate
        | undefined {
        return _.first(_.compact([this.institutionalTemplate, this.nynf3Template, this.professionalTemplate]));
    }
    private get transactions(): Transaction[] | undefined {
        return this.claimStore.findTransactions(this.claimId);
    }
    private get letters(): ProviderLetter[] | undefined {
        return this.claimStore.findLetters(this.claimId);
    }
    private get mails(): ProviderMail[] | undefined {
        return this.claimStore.findMails(this.claimId);
    }
    private get phones(): ProviderPhone[] | undefined {
        return this.claimStore.findPhones(this.claimId);
    }
    private get receiveFaxes(): ProviderReceiveFax[] | undefined {
        return this.claimStore.findReceiveFaxes(this.claimId);
    }
    private get sendFaxes(): ProviderSendFax[] | undefined {
        return this.claimStore.findSendFaxes(this.claimId);
    }
    private get emails(): ProviderSendEmail[] | undefined {
        return this.claimStore.findEmails(this.claimId);
    }
    private get seller(): ProviderSeller | undefined {
        return _.first(this.claimStore.findSeller(this.claimId));
    }
    private get chargemasterItems(): ProviderChargemasterItem[] | undefined {
        return this.claimStore.findChargemasterItems(this.claimId);
    }
    private get messages(): ProviderMessage[] | undefined {
        // Filter messages that are linked to this specific claim
        const allMessages = this.messageStore.findMessages({});
        return _.filter(allMessages, message => 
            _.some(message.links?.claims, claim => claim.id === this.claimId)
        );
    }

    private readonly renderClaimHaltedBanner = (): React.ReactElement | undefined => {
        const HALTED_CATEGORY_NAME = 'Claim Halted';

        const haltedCategoryId = _.find(this.messageStore.messageCategories, { name: HALTED_CATEGORY_NAME })?.id;
        if (!haltedCategoryId) {
            return;
        }
        const haltedMessage = _.find(this.messages || [], message => {
            const matchesHaltedCategory = _.some(
                message.links?.messageCategories || [],
                category => category.id === haltedCategoryId,
            );

            if (!matchesHaltedCategory) return false;

            const hasCommentsArray = Array.isArray(message.comments);
            const commentsCount = hasCommentsArray ? message.comments!.length : undefined;
            const fallbackCommentCount = typeof message.commentCount === 'number' ? message.commentCount : undefined;

            if (typeof commentsCount === 'number') return commentsCount === 1;
            if (typeof fallbackCommentCount === 'number') return fallbackCommentCount === 1;

            return false;
        });
        
        if (!haltedMessage?.id) {
            return;
        }

        const handleViewTask = (): void => {
            this.messagingContext.openPanel?.({
                claimId: this.claimId,
                messageId: haltedMessage.id,
            });
        };

        return (
            <S.Alert>
                <S.HaltedAlertContainer>
                    <Icon path={mdiPause} size={1} />
                    <S.AlertTitle>Claim Halted</S.AlertTitle>
                    <S.AlertDescription>This claim is halted and needs to be resumed manually.</S.AlertDescription>
                </S.HaltedAlertContainer>
                <S.AlertButton variant="primary" size='small' onClick={handleViewTask}>
                    View task
                </S.AlertButton>
            </S.Alert>
        );
    };

    private readonly renderClaimChart = (): React.ReactElement | undefined => {
        const { claim } = this.state;
        if (!claim) {
            return;
        }

        const round2 = (value: number) => Math.round(value * 100) / 100;

        // Calculate chart data from services using shared utility
        const calculated = this.calculateChartData();

        const charge = round2(calculated.charge || 0);
        const payerPaid = round2(calculated.payerPaid);
        const patientPaid = round2(calculated.patientPaid);
        const patientPending = round2(calculated.patientPending);
        const contractValue = calculated.contracted != null ? round2(calculated.contracted) : null;
        const payerAllowed = calculated.payerAllowed != null ? round2(calculated.payerAllowed) : null;
        const payerAdjusted = round2(
            calculated.payerAllowed &&
                calculated.contracted &&
                calculated.payerAllowed < calculated.contracted
                ? calculated.contracted - calculated.payerAllowed
                : 0,
        );
        // Only show adjustments if payer has adjudicated (payerPaid > 0)
        // Otherwise, the difference is "payer pending" not "adjustments"
        const hasPayerAdjudicated = payerPaid > 0;
        const adjustments = hasPayerAdjudicated ? round2(calculated.totalAdjustments) : 0;

        const chartData: ClaimChartData = {
            name: 'Claim',
            charge: charge,
            payerPaid,
            patientPaid,
            patientPending,
            contractValue: contractValue || null,
            payerAllowed: payerAllowed || null,
            payerAdjusted,
            adjustments,
        };

        return <HorizontalStackedBarChart chartData={chartData} />;
    };

    // Calculate chart data using shared utility from @enter/core
    private readonly calculateChartData = () => {
        const template = this.template;
        const collectionPayments = this.collections || [];
        const payments = this.payments || [];
        const chargemasterItems = this.chargemasterItems || [];

        if (template) {
            // Professional template
            if ('f24ServiceLine' in template) {
                const serviceLines = (template as ProviderProfessionalClaimTemplate).f24ServiceLine || [];
                return calculateChartDataFromProfessionalServices(
                    serviceLines,
                    payments,
                    collectionPayments,
                    chargemasterItems
                );
            }
            // Institutional template
            if ('f42ClaimLines' in template) {
                const serviceLines = (template as ProviderInstitutionalClaimTemplate).f42ClaimLines || [];
                return calculateChartDataFromInstitutionalServices(
                    serviceLines,
                    payments,
                    collectionPayments,
                    chargemasterItems
                );
            }
        }

        return getEmptyChartData();
    };

    private get openedMessages(): ProviderMessage[] {
        return _.filter(this.messages, m => !!m.open);
    }

    private fetchMessages = () => {
        this.messageStore.loadMessagesForClaim(this.claimId);
        // Load message categories when loading claim details
        this.messageStore.loadMessageCategories();
        if (!_.isEmpty(this.openedMessages)) {
            this.setState({
                showAlert: true,
            });
        }
    };

    private fetchClaimDetails = async () => {
        return this.claimStore.loadClaimDetail(this.claimId, (this.state.legacy === 'true'));
        // if(this.state.legacy === 'true') {
        //     return this.claimStore.loadClaimDetail(this.claimId);
        // } else {
        //     const claimDetails: IClaimDetail | undefined = await this.claimStore.loadProviderClaimWithSuperFastClaimDetails(this.claimId);
        //     if(claimDetails) {
        //         this.setState(state => ({
        //             ...state,
        //             claim: claimDetails.claim,
        //         }));
        //     }
        // }
    };

    private fetchData = async (): Promise<void> => {
        if (!this.state.claim || !this.state.claimDetail) {
            try {
                this.setState({ loading: true });

                this.fetchMessages();

                let claimDetail: IClaimDetail | undefined;

                if(this.state.legacy === 'true') {
                    // query for claim in case already available
                    let claim = this.claimStore.findClaim(this.claimId);
                    if (!claim) {
                        claim = await this.claimStore.loadClaim(this.claimId);
                    }
                    this.setState(state => ({
                        ...state,
                        claim,
                    }));
                    claimDetail = await this.fetchClaimDetails();
                    this.setState({
                        claimDetail
                    });
                } else {
                    claimDetail = await this.fetchClaimDetails();
                    this.setState(state => ({
                        ...state,
                        claimDetail,
                        claim: claimDetail?.claim,
                    }));
                }
            } catch (err) {
                if (err && err.payload) {
                    const { payload } = err;
                    if (payload.code === 'err.application_access_token_has_expired') {
                        // NB: continue silently
                    } else if (payload.code === 'err.access_denied' || payload.code === 'err.claim_not_found') {
                        if (this.props.history) {
                            this.props.history.push(`/claims`);
                        }
                    } else {
                        this.dialogContext.showError(err);
                    }
                } else if (_.get(err, 'name') === 'AbortError' || _.get(err, 'name') === 'TypeError') {
                    // NB: continue silently
                    // AbortError is from cancelled requests;
                    // TypeError from CORS errors (failed to fetch);
                } else {
                    this.dialogContext.showError(err);
                }
            } finally {
                this.setState({ loading: false });
            }
        }
    };

    private readonly handleDismiss = (): void => {
        this.setState({
            showAlert: false,
        });
    };

    private readonly renderLoader = (): React.ReactElement => (
        <Fade in={true}>
            <S.Loader>
                <LoadingMeter />
            </S.Loader>
        </Fade>
    );
    private readonly renderPage = (): React.ReactElement => {
        const { showAlert, claimDetail, loading } = this.state;
        const isMissingClaim = claimDetail && !claimDetail?.professionalClaim && !claimDetail?.institutionalClaim;
        const hasValidationErrors = claimDetail && claimDetail.claim && claimDetail.claim.claimValidated && claimDetail.claim.claimValidationErrors && claimDetail.claim.claimValidationErrors.length > 0;
        const { history } = this.props;

        const receiptIconVisible = this.state.claimDetail && this.state.claimDetail.patients && this.state.claimDetail.patients.length > 0;
        // Extract accountId from match params or fallback to extracting from URL pathname
        let accountId = this.props.match.params.accountId;
        if (!accountId) {
            const pathParts = window.location.pathname.split('/').filter(Boolean);
            accountId = pathParts.length > 0 && pathParts[0].length === 36 ? pathParts[0] : undefined;
        }
        // Use absolute paths since we're navigating between sibling routes
        const claimsPath = accountId ? `/${accountId}/claims` : '/claims';
        const breadcrumbLink = receiptIconVisible && accountId
            ? `/${accountId}/patients/${this.state.claimDetail.patients[0].id}/statement`
            : receiptIconVisible
            ? `/patients/${this.state.claimDetail.patients[0].id}/statement`
            : claimsPath;

        const handleEditClaimFromValidationErrorsBanner = (): void => {
            const editPath = accountId ? `/${accountId}/claims/${this.claimId}/edit` : `/claims/${this.claimId}/edit`;
            history?.push(editPath);
        };

        return (
            <PageLayout>
                {this.state.claim && <ClaimBanner claim={this.state.claim} />}
                {this.renderClaimHaltedBanner()}
                {hasValidationErrors && (
                    <ValidationErrorsBanner onEditClaim={handleEditClaimFromValidationErrorsBanner} />
                )}
                {isMissingClaim && !hasValidationErrors && (
                    <MissingClaimBanner />
                )}
                <S.StyledPageLayoutRow>
                    {((this.state.claim && (this.state.claim.patientFirst || this.state.claim.patientLast)) ||
                      (this.state.claim && this.state.claim.accountNumber) ||
                      (this.state.claimDetail && this.state.claimDetail.patients && this.state.claimDetail.patients.length > 0)) && (
                        <PageLayoutBreadcrumb>
                            <PageLayoutBreadcrumbLink to={claimsPath}>Claims</PageLayoutBreadcrumbLink>
                            {this.state.claim && (this.state.claim.patientFirst || this.state.claim.patientLast) && (() => {
                                const baseName = _.compact([this.state.claim!.patientFirst, this.state.claim!.patientLast]).join(' ');
                                const fullName = baseName ? _.compact([baseName, this.state.claim!.patientSuffix]).join(' ') : '';
                                return (
                                    <PageLayoutBreadcrumbLink to={breadcrumbLink}>
                                        {fullName}
                                        {receiptIconVisible ? <Emoji type="receipt" title="View Statement"/> : <></>}
                                    </PageLayoutBreadcrumbLink>
                                );
                            })()}
                            {
                                this.state.claim && this.state.claim.accountNumber
                                && <S.ClaimNumber>(Claim: {this.state.claim.accountNumber})</S.ClaimNumber>
                            }
                            {
                                this.state.claimDetail && this.state.claimDetail.patients && this.state.claimDetail.patients.length > 0
                                && <S.ClaimNumber>(Patient: {this.state.claimDetail.patients[0].externalId})</S.ClaimNumber>
                            }
                        </PageLayoutBreadcrumb>
                    )}
                    {this.renderMainHeader()}
                    {this.renderSubHeader()}
                    {this.renderClaimBundle()}
                </S.StyledPageLayoutRow>
                {showAlert && (
                    <S.StyledAlertBar
                        messages={this.openedMessages}
                        onDismiss={this.handleDismiss}
                        claimId={this.claimId}
                    />
                )}
                {/* New financial chart row */}
                {!isMissingClaim && (
                    <S.StyledPageLayoutRow data-overview-layout-row>
                        {this.renderClaimChart()}
                    </S.StyledPageLayoutRow>
                )}
                {!isMissingClaim && (
                    <S.OverviewLayoutRow>
                        <S.OverviewLayoutColumn>{this.renderPersonalOverview()}</S.OverviewLayoutColumn>
                        <S.OverviewLayoutColumn>{this.renderInsuranceOverview()}</S.OverviewLayoutColumn>
                    </S.OverviewLayoutRow>
                )}

                <S.StyledPageLayoutRow>{!isMissingClaim && this.renderRecordOfServices()}</S.StyledPageLayoutRow>
                <S.StyledPageLayoutRow>{this.renderClaimTimelineTable()}</S.StyledPageLayoutRow>
                {/* {!isMissingClaim && <S.StyledPageLayoutRow>{this.renderBillingTimelineTable()}</S.StyledPageLayoutRow>} */}
            </PageLayout>
            
        );
    };
    private readonly renderMainHeader = (): React.ReactElement => {
        const { claim, claimDetail } = this.state;
        const accountId = this.props.match.params.accountId;
        const { history } = this.props;

        return (
            <S.MainHeader>
                <S.MainGroup>
                    <S.Title>{this.renderServiceDates()}</S.Title>
                    {this.renderResponsibilityBadge()}
                    {this.renderCloseoutBadge()}
                    {this.renderMaxResponsibility()}
                </S.MainGroup>
                <S.Spacer />
                <ClaimMainHeaderActions
                    claimId={this.claimId}
                    claim={claim}
                    claimDetail={claimDetail}
                    accountId={accountId}
                    history={history}
                />
                
            </S.MainHeader>
        );
    };

    private isLoaded(keys: ClaimDetailsKey[]) {
        return keys.every(key => this.claimStore.claimDetailsLoader.get(key));
    }

    private readonly renderSubHeader = (): React.ReactElement | undefined => {
        const loaded = this.isLoaded(['providers', 'facilities', 'sellers']);

        if (this.state.claim && loaded) {
            return (
                <S.SubHeader>
                    {this.renderClaimTypeBadge()}
                    {this.renderProviders()}
                    {this.renderFacility()}
                    {this.renderSeller()}
                </S.SubHeader>
            );
        }
    };

    private readonly renderClaimBundle = (): React.ReactElement | undefined => {
        const { claim } = this.state;
        if (claim?.claimBundleId && claim?.id) {
            return (
                <ClaimBundle
                    bundleId={claim.claimBundleId}
                    claimId={claim.id}
                    onLoadBundle={(bundleId) => this.claimStore.loadClaimBundle(bundleId)}
                />
            );
        }
    };

    private readonly renderServiceDates = (): string | undefined => {
        const { claim } = this.state;
        if (!claim) {
            return;
        }
        if (claim.fromDos && claim.throughDos && claim.fromDos !== claim.throughDos) {
            return `${formatDate(claim.fromDos, 'MMM d')} – ${formatDate(claim.throughDos, 'MMM d, yyyy')}`;
        } else if (claim.fromDos) {
            return formatDate(claim.fromDos, 'MMM d, yyyy');
        }
    };

    private readonly renderResponsibilityBadge = (): React.ReactElement | undefined => {
        if (this.state.claim) {
            return (
                <Tooltip
                    direction="top"
                    variant="dark"
                    title={
                        this.state.claim.claimResponsibilityType ? (
                            <span>{getResponsibilityDescription(this.state.claim.claimResponsibilityType)}</span>
                        ) : undefined
                    }>
                    <S.StatusBadge size="large" responsibility={this.state.claim.claimResponsibilityType} />
                </Tooltip>
            );
        }
    };
    private readonly renderCloseoutBadge = (): React.ReactElement | undefined => {
        const { claim } = this.state;
        if (!claim) {
            return;
        }

        // Hide closeout when responsibility is NONE and closeout is OPEN
        if (
            claim.closeoutType === ClaimCloseoutType.OPEN &&
            claim.claimResponsibilityType === ClaimResponsibilityType.NONE
        ) {
            return;
        }

        const label = `Closeout: ${claim.closeoutType}`;

        const items: IActionMenuBadgeItem[] = [
            {
                label: 'Modify Closeout',
                action: async () => {
                    const currentClaim = this.state.claim;
                    if (!currentClaim) {
                        return;
                    }

                    const handleClose = (): void => {
                        this.dialogContext.hideDialog();
                    };

                    const handleSubmit = (updated: ProviderClaim): void => {
                        this.setState(state => ({
                            ...state,
                            claim: {
                                ...state.claim,
                                closeoutType: updated.closeoutType,
                                closeoutDescription: updated.closeoutDescription,
                            } as ProviderClaim,
                        }));
                        this.dialogContext.hideDialog();
                    };

                    this.dialogContext.showDialog({
                        props: {
                            onClose: handleClose,
                            disableBackdropClick: true,
                            disableEscapeKeyDown: true,
                            renderTitle: 'Modify Closeout',
                        },
                        Component: (
                            <ProviderCloseoutDialog
                                claim={currentClaim}
                                onCancel={handleClose}
                                onSubmit={handleSubmit}
                            />
                        ),
                    });
                },
            },
        ];

        return (
            <S.BadgeWrapper>
                <ActionMenuBadge label={label} items={items} />
            </S.BadgeWrapper>
        );
    };
    private readonly renderClaimTypeBadge = (): React.ReactElement | undefined => {
        const { claim } = this.state;
        if (claim && claim.type) {
            let claimType = '';
            if (claim.type === ClaimType.INSTITUTIONAL) {
                claimType = ' CMS-1450 / UB04';
            } else if (claim.type === ClaimType.PROFESSIONAL) {
                claimType = ' CMS-1500';
            } else if (claim.type === ClaimType.NYNF3) {
                claimType = 'NF-3';
            }

            const primaryPayerSubscriber = _.first(this.payerSubscribers);
            const primarySubscriberPatient = _.first(this.subscriberPatients);

            const label = `${claimType || ''} Claim ID – ${truncateUuid(this.claimId)}`;
            const items: IActionMenuBadgeItem[] = _.compact([
                claim.type === ClaimType.PROFESSIONAL &&
                this.claimPIND?.id &&
                primaryPayerSubscriber?.id &&
                primarySubscriberPatient?.id
                    ? {
                          label: 'View CMS-1500 document',
                          action: async () => {
                              if (!this.claimPIND?.id || !primaryPayerSubscriber?.id || !primarySubscriberPatient?.id) {
                                  this.dialogContext.showError(new Error('Can`t fetch the document'));
                                  return;
                              }

                              try {
                                  const documentUrl = await ClaimService.viewCMS1500({
                                      id: this.claimPIND?.id,
                                      payerSubscriberId: primaryPayerSubscriber?.id,
                                      subscriberPatientId: primarySubscriberPatient?.id,
                                  });

                                  window.open(documentUrl?.url, '_blank');
                              } catch (err) {
                                  this.dialogContext.showError(err);
                              }
                          },
                      }
                    : undefined,
            ]);

            return <ActionMenuBadge label={label} items={items} />;
        }
    };
    private readonly renderProviders = () => {
        if (this.providers) {
            const accountId = this.props.match.params.accountId;
            return _.map(this.providers, provider => (
                <ProviderBadge
                    key={provider.id}
                    id={provider.id}
                    first={provider.first}
                    suffix={provider.suffix}
                    last={provider.last}
                    type={provider.type}
                    organization={provider.organization}
                    path={accountId ? `/${accountId}/claims/by-provider/${provider.id}` : `/claims/by-provider/${provider.id}`}
                    tooltip="Find claims for this Provider"
                />
            ));
        } else {
            return <MissingIdBadge>No provider(s)</MissingIdBadge>;
        }
    };
    private readonly renderFacility = () => {
        if (this.facility) {
            const accountId = this.props.match.params.accountId;
            return (
                <FacilityBadge
                    id={this.facility.id}
                    name={this.facility.name}
                    path={accountId ? `/${accountId}/claims/by-facility/${this.facility.id}` : `/claims/by-facility/${this.facility.id}`}
                    tooltip="Find claims for this Facility"
                />
            );
        } else {
            return <MissingIdBadge>No facility</MissingIdBadge>;
        }
    };
    private readonly renderSeller = (): React.ReactElement | undefined => {
        if (this.seller && this.seller.id && this.seller.first && this.seller.last) {
            const { id, first, last } = this.seller;
            const accountId = this.props.match.params.accountId;
            return (
                <SellerBadge
                    id={id}
                    first={first}
                    last={last}
                    path={accountId ? `/${accountId}/claims/by-seller/${id}` : `/claims/by-seller/${id}`}
                    tooltip="View claims for Seller"
                />
            );
        }
    };

    private readonly renderMaxResponsibility = (): React.ReactElement | undefined => {
        if(this.state.claim && this.state.claim.id) {

            const label = "Max PR: " + ((this.state.claim.maximumGuarantorResponsibility && this.state.claim.maximumGuarantorResponsibility.amount) ? formatAmount(this.state.claim.maximumGuarantorResponsibility?.amount) : 'None');

            const items: IActionMenuBadgeItem[] = [
                {
                    label: 'Modify Max PR',
                    action: async() => {
                        const handleClose = (): void => {
                            this.dialogContext.hideDialog();
                        };

                        const handleSubmit = (): void => {
                            this.dialogContext.hideDialog();
                            window.location.reload(); //For now, just reload the screen
                        }

                        this.dialogContext.showDialog({
                            props: {
                                onClose: handleClose,
                                disableBackdropClick: true,
                                disableEscapeKeyDown: true,
                                renderTitle: 'Modify Maximum Patient Responsibility',
                            },
                            Component: <MaxProviderPRDialog
                                maximumGuarantorResponsibility={this.state.claim?.maximumGuarantorResponsibility}
                                claimId={this.state.claim && this.state.claim.id ? this.state.claim.id: ''}
                                onClose={handleClose}
                                onSubmit={handleSubmit}
                            />,
                        });
                    }
                }
            ];

            return <ActionMenuBadge label={label} items={items} Symbol={<Emoji type="moneyBag" />} />;
        }
    }
    private readonly renderPersonalOverview = (): React.ReactElement | undefined => {
        const primaryPayerSubscriber = this.payerSubscribers && this.payerSubscribers[0];
        const loaded = this.isLoaded(['guarantors', 'subscriberPatients', 'payerSubscribers', 'invoices']);

        const patientInvoice = _.find(this.invoices, { invoiceType: InvoiceType.PATIENT });

        return (
            <ClaimPersonalOverview
                patientInvoice={patientInvoice}
                claimId={this.claimId}
                loading={!loaded || !this.templateLoaded}
                guarantor={this.guarantor}
                subscriberPatient={_.first(this.subscriberPatients)}
                payerSubscriber={primaryPayerSubscriber}
                institutionalTemplate={this.institutionalTemplate}
                collections={this.collections}
            />
        );
    };
    private readonly renderInsuranceOverview = (): React.ReactElement | undefined => {
        const loaded = this.isLoaded([
            'payers',
            'payerSubscribers',
            'subscribers',
            'subscriberPatients',
            'eligibilities',
        ]);

        const thirdPartyAdministrators = [this.state.claimDetail?.professionalClaim?.primaryThirdPartyAdministratorPayer, this.state.claimDetail?.professionalClaim?.secondaryThirdPartyAdministratorPayer, this.state.claimDetail?.professionalClaim?.tertiaryThirdPartyAdministratorPayer];

        return (
            <InsuranceOverview
                loading={!loaded}
                payers={this.payers}
                payerSubscribers={this.payerSubscribers}
                subscribers={this.subscribers}
                subscriberPatients={this.subscriberPatients}
                thirdPartyAdministrators={thirdPartyAdministrators}
                eligibilities={this.eligibilities}
            />
        );
    };
    private readonly renderFinancialOverview = (): React.ReactElement | undefined => {
        // calculate goal by adding up accepted amounts from ChargemasterItems which match service line items
        // let goal = 0;
        // if (this.template) {
        //     const serviceLines: { chargemasterItemId?: string }[] = _.concat(
        //         [],
        //         _.get(this.template, 'f15ServiceLines'),
        //         _.get(this.template, 'f24ServiceLine'),
        //         _.get(this.template, 'f42ClaimLines'),
        //     );
        //     goal = _.reduce(
        //         serviceLines,
        //         (r, line) => {
        //             const item = _.find(this.chargemasterItems, { id: line?.chargemasterItemId });
        //             return r + parseAmount(item?.expected);
        //         },
        //         0,
        //     );
        // }

        const goal = parseAmount(this.state.claim?.chargemasterAllowedAmount);

        const allowed = parseAmount(this.state.claim?.allowedAmount);

        const totalCharges = parseAmount(this.state.claim?.chargeAmount);

        const payerPaid = parseAmount(this.state.claim?.payerAmount);

        const patientPaid = parseAmount(this.state.claim?.patientInvoicePaidAmount);

        const patientAmount = parseAmount(this.state.claim?.patientInvoiceAmount);

        let remaining = 0;

        // a patient remaining balance should not be calculated on the UI when adjudication has not completed. When calculating the patient remaining amount, the collection status needs to be taken into account to determine if the claim has been fully adjudicated before we alert to the provider that a refund is required. This can be done by having the patient remaining amount due be $0 if the collection status is null, NOT_STARTED , SECONDARY_BILL , or TERTIARY_BILL . In this case, the collection status is NOT_STARTED .
        // https://app.clickup.com/t/2pc0bh6
        if (
            this.state.claim?.collectionStatus &&
            ![
                ProviderCollectionStatusType.NOTSTARTED,
                ProviderCollectionStatusType.SECONDARYBILL,
                ProviderCollectionStatusType.TERTIARYBILL,
            ].includes(this.state.claim.collectionStatus)
        ) {
            remaining = (this.state.claim?.claimAdjudicationDate) ? patientAmount - patientPaid : Math.max(0, patientAmount - patientPaid);
        }

        const financial = {
            total: {
                name: 'Charge',
                amount: totalCharges,
            },
            segments: [
                {
                    name: 'Payer Paid',
                    amount: payerPaid,
                    infoMessage: this.state.claim?.claimAdjudicationDate
                        ? `Adjudication: ${formatDate(
                              this.state.claim.claimAdjudicationDate,
                              'MMM dd, yyyy hh:mm',
                          )}`
                        : `Adjudication: N/A`,
                },
                { name: 'Patient Paid', amount: patientPaid },
                {
                    shortLabel: 'Remaining',
                    name: 'Patient Remaining',
                    amount: remaining,
                    showRefundWarning: true,
                },
            ],
            goal: {
                name: 'Accepted',
                amount: goal,
            },
        };
        return undefined;
    };

    private readonly renderRecordOfServices = (): React.ReactElement => {
        const loaded = this.isLoaded(['providers', 'chargemasterItems', 'payments']);
        return (
            <RecordOfServices
                loading={!loaded || !this.templateLoaded}
                template={this.template}
                providers={this.providers}
                chargemasterItems={this.chargemasterItems}
                payments={this.payments}
                claimPatientPaid={this.state.claim?.patientInvoicePaidAmount}
            />
        );
    };

    private readonly renderClaimPayerSummary = (): React.ReactElement => {
        const loaded = this.isLoaded(['providers', 'chargemasterItems', 'payments']);
        //Bug fix: claim payer summary is inaccurate if we have old professional payments, so hide it, if so
        let hasOldProfessionalPayments = false;
        if(loaded) {
            hasOldProfessionalPayments = this.claimStore.hasOldProfessionalPayments(this.claimId);
        }
        if(hasOldProfessionalPayments) {
            return (<></>);
        } else {
            return (
                <ClaimPayerSummary
                    loading={!loaded || !this.templateLoaded}
                    template={this.template}
                    chargemasterItems={this.chargemasterItems}
                    payments={this.payments}
                    providers={this.providers}
                />
            );
        }
    };

    private readonly renderClaimTimelineTable = (): React.ReactElement => {
        return (
            <ClaimTimelineTable
                loading={this.state.loading}
                loadingBundle={this.state.loadingBundle}
                claim={this.state.claim}
                claimPind={this.claimPIND}
                codings={this.codings}
                collections={this.collections}
                comments={this.comments}
                documents={this.documents}
                statuses={this.statuses}
                eligibilities={this.eligibilities}
                hl7Messages={this.hl7Messages}
                invoices={this.invoices}
                letters={this.letters}
                messages={this.messages}
                mails={this.mails}
                payments={this.payments}
                phones={this.phones}
                receiveFaxes={this.receiveFaxes}
                sendFaxes={this.sendFaxes}
                emails={this.emails}
                transmissions={this.transmissions}
                transactions={this.transactions}
                claimBundle={this.state.claimBundle}
                onLoadClaimBundle={this.onLoadClaimBundle}
                payerSubscribers={this.payerSubscribers}
                subscriberPatients={this.subscriberPatients}
                dialogContext={this.dialogContext}
            />
        );
    };

    private readonly onLoadClaimBundle = async (bundleId: string) => {
        this.setState({ loadingBundle: true });

        try {
            const claimBundle = await this.claimStore.loadClaimBundle(bundleId);

            this.setState({ claimBundle });
        } finally {
            this.setState({ loadingBundle: false });
        }
    };

    private readonly renderBillingTimelineTable = (): React.ReactElement => {
        const loaded = this.isLoaded([
            'checkoutCharges',
            'creditCards',
            'guarantors',
            'invoices',
            'paymentPlans',
            'transactions',
            'payments',
        ]);

        return (
            <BillingTimelineTable
                loading={!loaded}
                checkoutCharges={this.checkoutCharges}
                claimId={this.claimId}
                creditCards={this.creditCards}
                guarantor={this.guarantor}
                invoices={this.invoices}
                paymentPlans={this.paymentPlans}
                transactions={this.transactions}
                payments={this.payments}
                Header={this.renderHeader()}
            />
        );
    };

    private readonly renderHeader = (): React.ReactElement => {
        const patientInvoice = _.find(this.invoices, { invoiceType: InvoiceType.PATIENT });
        return <GuarantorHeader claimId={this.claimId} guarantor={this.guarantor} patientInvoice={patientInvoice} />;
    };
}

interface IClaimMainHeaderActionsProps {
    claimId: string;
    claim: ProviderClaim | undefined;
    claimDetail: IClaimDetail | undefined;
    accountId?: string;
    history?: RouterProps<IMatchParams>['history'];
}

const ClaimMainHeaderActions: React.FC<IClaimMainHeaderActionsProps> = ({
    claimId,
    claim,
    claimDetail,
    accountId,
    history,
}) => {
    const [aiOpen, setAiOpen] = React.useState(false);

    const { checking, connected } = useAiExtensionConnectionStatus();
    const isAiDisabled = checking || connected === false;
    const aiDisabledTooltip = 'You need to install and turn on your Ctrl Enter extension to use this feature';

    const handleEditClick = () => {
        const editPath = accountId ? `/${accountId}/claims/${claimId}/edit` : `/claims/${claimId}/edit`;
        history?.push(editPath);
    };

    return (
        <S.ActionButtons>
            <S.EditButton variant="primary" size="medium" onClick={handleEditClick}>
                Edit claim
            </S.EditButton>
            <NewMessageButton claimId={claimId} />
            <S.AskButtonWrapper>
                <Tooltip direction="top" variant="dark" title={<span>{isAiDisabled ? aiDisabledTooltip : 'Ask AI'}</span>}>
                    <S.CustomAIButton onClick={() => {
                        if (isAiDisabled) return;
                        setAiOpen(true);
                    }} disabled={isAiDisabled}>
                        <img src={enterLogo} alt="Enter Services" /> Talk to Claim
                    </S.CustomAIButton>
                </Tooltip>
            </S.AskButtonWrapper>
            {claim && (
                <AiAssistant
                    open={aiOpen}
                    onClose={() => setAiOpen(false)}
                    entity="Claim"
                    subjectLabel="claim"
                    suggestionsKey="default"
                    contextData={claimDetail?.claim ?? claim}
                    utilityKey="claimDetails:Provider"
                />
            )}
        </S.ActionButtons>
    );
};

const ClaimBanner: React.FC<{ claim: ProviderClaim }> = ({ claim }) => {
    const messagingContext = useContext(ProviderMessagingContext);

    const handleCreateTask = (): void => {
        if (!claim?.id) return;

        messagingContext.openPanel?.({
            claimId: claim.id,
            isComposeOpen: true,
            defaultSubject: 'Claim needs Human Intervention',
            defaultBody: 'This claim must be flagged as needing human intervention. Please review.',
        });
    };

    if (claim.collectionStatus === ProviderCollectionStatusType.HUMANINTERVENTIONNEEDED) {
        return (
            <S.Alert>
                <S.HaltedAlertContainer>
                    <Icon path={mdiAccountOutline} size={1} />
                    <S.AlertTitle>Human Intervention Needed</S.AlertTitle>
                </S.HaltedAlertContainer>
                <S.AlertButton variant="primary" size='small' onClick={handleCreateTask}>
                    Create task
                </S.AlertButton>
            </S.Alert>
        );
    }

    if (claim?.closeoutType === ClaimCloseoutType.CLOSED) {
        return (
            <S.Alert type="success">
                <S.HaltedAlertContainer>
                    <Icon path={mdiCheck} size={1} />
                    <S.AlertTitle>This claim is closed.</S.AlertTitle>
                    {claim.closeoutDescription && (
                        <S.AlertDescription>
                            {renderString(claim.closeoutDescription, {
                                type: 'String',
                                normalized: true,
                            })}
                        </S.AlertDescription>
                    )}
                </S.HaltedAlertContainer>
            </S.Alert>
            
        );
    }

    return null;
};

const ValidationErrorsBanner: React.FC<{ onEditClaim?: () => void }> = ({ onEditClaim }) => {
    return (
        <S.Alert>
            <S.HaltedAlertContainer>
                <Icon path={mdiPencilOutline} size={1} />
                <div>
                    <S.AlertTitle>Claim has validation errors that have prevented it from being automatically generated.</S.AlertTitle>
                </div>
            </S.HaltedAlertContainer>
            {onEditClaim && (
                <S.AlertButton variant="primary" size='small' onClick={onEditClaim}>
                    Edit claim
                </S.AlertButton>
            )}
        </S.Alert>
        
    );
};

const MissingClaimBanner: React.FC = () => {
    return (
        <S.Alert>
            <S.HaltedAlertContainer>
                <Icon path={mdiAlertCircle} size={1} />
                <S.AlertTitle>Claim details are not available because the claim is currently in progress of getting created.</S.AlertTitle>
            </S.HaltedAlertContainer>
        </S.Alert>
        
    );
};

const NewMessageButton = ({ claimId }: { claimId: string }) => {
    const messagingContext = useContext(ProviderMessagingContext);
    const handleNewMessage = () => {
        messagingContext.openPanel?.({
            claimId,
            isComposeOpen: true,
        });
    };

    return (
        <Tooltip direction="top" size="large" variant="dark" title={<span>Send a message about this claim</span>}>
            <S.ComposeButton variant="primary" size="medium" onClick={handleNewMessage}>
                <S.ComposeIcon path={mdiPlus} size={0.75} />
                <S.ComposeLabel>New message</S.ComposeLabel>
            </S.ComposeButton>
        </Tooltip>
    );
};

const ClaimDetailPageWithRouter = withRouter(ClaimDetailPage);

export const ClaimDetail: React.FC<IObservedRootStore> = inject('rootStore')(
    observer(
        (props: IObservedRootStore): React.ReactElement => {
            const { rootStore } = props;
            if (!rootStore) {
                return <></>;
            }
            const authContext = useContext(AuthContext);
            const dialogContext = useContext(DialogContext);
            const messagingContext = useContext(ProviderMessagingContext);
            
            return (
                <ClaimDetailPageWithRouter
                    authContext={authContext}
                    dialogContext={dialogContext}
                    messagingContext={messagingContext}
                    rootStore={rootStore}
                />
            );
        },
    ),
);

export const ClaimPersonalOverview: React.FC<{
    claimId: string;
    loading: boolean;
    patientInvoice: Invoice | undefined;
    guarantor: ProviderGuarantor | undefined;
    subscriberPatient: ProviderSubscriberPatient | undefined;
    payerSubscriber: ProviderPayerSubscriber | undefined;
    institutionalTemplate: ProviderInstitutionalClaimTemplate | undefined;
    collections: ProviderCollectionPayment[] | undefined;
}> = inject('rootStore')(
    observer(props => {
        const {
            claimId,
            loading,
            guarantor,
            subscriberPatient,
            payerSubscriber,
            institutionalTemplate,
            patientInvoice,
            collections,
        } = props;
        const dialogContext = useContext(DialogContext);
        const toastContext = useContext(ToastContext);

        const rootStore: RootStore = _.get(props, 'rootStore');
        if (!rootStore) {
            return <></>;
        }

        // Extract accountId from URL
        const getAccountId = (): string | undefined => {
            const pathParts = window.location.pathname.split('/').filter(Boolean);
            return pathParts.length > 0 && pathParts[0].length === 36 ? pathParts[0] : undefined;
        };
        const accountId = getAccountId();

        const { claimStore } = rootStore;

        const handleGuarantorLink = async () => {
            try {
                if (guarantor && guarantor.id) {
                    const tempUrl = await claimStore.getGuarantorViewBillUrl(guarantor.id);
                    if (tempUrl && tempUrl.url) {
                        window.open(tempUrl.url, '_blank');
                        return;
                    }
                }
            } catch (err) {
                dialogContext.showError(err);
            }
            toastContext.notify?.('Billing document is currently unavailable.', 'right');
        };

        const handleCloseDialog = () => {
            dialogContext.hideDialog();
        };

        const handleGuarantorCheckout = async () => {
            if (!guarantor?.id) {
                return;
            }

            try {
                const shortUrl = await claimStore.getGuarantorCheckoutUrl(guarantor.id);
                if (shortUrl && shortUrl.url) {
                    window.open(shortUrl.url, '_blank');
                }
            } catch (err) {
                const parsedError = await parseError(err);
                if (parsedError.payload.code === 'err.billing_not_active_for_invoice') {
                    dialogContext.showDialog({
                        props: {
                            onClose: handleCloseDialog,
                            disableBackdropClick: true,
                            disableEscapeKeyDown: true,
                            renderTitle: 'Billing is not active for this invoice yet',
                        },
                        Component: (
                            <ErrorDialog
                                onClose={handleCloseDialog}
                                payload={{
                                    message: 'If you feel this is in error, please reach out to support.',
                                    hideTrace: true,
                                }}
                            />
                        ),
                    });
                } else {
                    dialogContext.showError(err);
                }
            }
        };

        const handleRefund = () => {
            dialogContext.showDialog({
                props: {
                    onClose: handleCloseDialog,
                    disableBackdropClick: true,
                    disableEscapeKeyDown: true,
                    renderTitle: 'Refund Payment',
                },
                Component: (
                    <CollectionRefundDialog
                        onClose={handleCloseDialog}
                        onSubmit={handleCloseDialog}
                        collectionPayments={collections ? collections.filter(c=>c.source !== CollectionPaymentSourceType.TERMINAL) : []}
                    />
                ),
            });
        };

        const handleRecordPayment = (): void => {
            dialogContext.showDialog({
                props: {
                    onClose: handleCloseDialog,
                    disableBackdropClick: true,
                    disableEscapeKeyDown: true,
                    renderTitle: 'Record Payment',
                },
                Component: <CollectionPaymentDialog onClose={handleCloseDialog} claimId={claimId} />,
            });
        };

        const handleDiscount = () => {
            if (patientInvoice) {
                dialogContext.showDialog({
                    props: {
                        onClose: handleCloseDialog,
                        disableBackdropClick: true,
                        disableEscapeKeyDown: true,
                        renderTitle: 'Set Discount Amount',
                    },
                    Component: <InvoiceItemDialog onClose={handleCloseDialog} invoice={patientInvoice} />,
                });
            }
        };

        // <S.GuarantorAmount>
        //     {patientInvoice && (
        //         <>
        //             {renderAmount(patientInvoice.paidAmount)} / {renderAmount(patientInvoice.amount)}
        //         </>
        //     )}
        // </S.GuarantorAmount>

        return (
            <PersonalOverview
                loading={loading}
                guarantor={guarantor}
                subscriberPatient={subscriberPatient}
                payerSubscriber={payerSubscriber}
                institutionalTemplate={institutionalTemplate}
                onGuarantorCheckout={handleGuarantorCheckout}
                onGuarantorLink={handleGuarantorLink}
                patientInvoice={patientInvoice}
                accountId={accountId}
                guarantorActions={_.compact([
                    // {
                    //     onClick: handleRefund,
                    //     icon: <Emoji type="repeat" title="Refund" />,
                    //     label: 'Record Refund',
                    // },
                    // {
                    //     onClick: handleRecordPayment,
                    //     icon: <Emoji type="writingHand" title="Record Payment" />,
                    //     label: 'Record Payment',
                    // },
                    // patientInvoice
                    //     ? {
                    //           onClick: handleDiscount,
                    //           icon: <Emoji type="moneyWings" title="Set Discount" />,
                    //           label: 'Set Discount',
                    //       }
                    //     : undefined,
                ])}
            />
        );
    }),
);
